import RootStore from '30.quickConnect.Stores/RootStore';
import CustomLogger from '80.quickConnect.Core/logger/customLogger';
import { FormViewer } from '90.quickConnect.Models/models';
import { OuDecisionKind } from '90.quickConnect.Models/enums';
import { OuDecision } from '90.quickConnect.Models/models/user/ouDecision';
import { OrganizationalUnitViewer } from '90.quickConnect.Models/models/user/organizationalUnitViewer';
import {
  isOuCodeAncestor,
  isSameOuCode,
  normalizeOuCode,
  ouCodeDepth,
} from '80.quickConnect.Core/helpers/organizationalUnits/ouCode';

/** Formulaire à contrôler : par son identifiant s'il est connu, sinon par son nom (deeplink) */
export type OuFormTarget = {
  formId?: string;
  name: string;
};

const OPEN: OuDecision = { kind: OuDecisionKind.Open };
const UNAUTHORIZED: OuDecision = { kind: OuDecisionKind.Unauthorized };
const switchTo = (ou: OrganizationalUnitViewer): OuDecision => ({ kind: OuDecisionKind.SwitchTo, ou });

/**
 * Contrôle d'UO avant l'ouverture d'un formulaire, la reprise d'un brouillon, la duplication d'une déclaration ou
 * l'ouverture d'une inbox (portage de OUAccessChecker Android). Sans la feature multi-UO, tout s'ouvre directement.
 * Les popups correspondant aux décisions sont affichées par OuSwitchPrompt. Store sans état : rien à observer ni à
 * réinitialiser.
 */
class OuAccessStore {
  private static readonly TAG = '30.quickConnect.Stores/RootStore/OuAccessStore/index.ts';

  RootStore: RootStore;

  logger: CustomLogger;

  constructor(rootStore: RootStore, logger: CustomLogger) {
    this.RootStore = rootStore;
    this.logger = logger;
  }

  isEnabled = (): boolean => this.RootStore.LoginStore.isMultiOUFeatureEnabled();

  /**
   * Formulaire : ouverture directe s'il figure dans la liste de l'UO courante (formulaires de l'UO et de ses parentes),
   * sinon bascule vers une UO accessible qui le propose (UO de rattachement en priorité), sinon refus.
   * Les listes des UO sont celles de l'API (getformviewer par UO, en cache) : sans liste lisible, ouverture directe.
   */
  decisionForForm = async ({ formId, name }: OuFormTarget): Promise<OuDecision> => {
    if (!this.isEnabled()) return OPEN;

    const {
      LoginStore: { effectiveOuCode, ouTree, connected },
      FormStore: { getFormsListForOu },
    } = this.RootStore;
    // Par identifiant quand il est connu : un homonyme dans une autre UO n'est pas le même formulaire
    const isSameForm = (form: FormViewer) => (formId ? form.id === formId : form.name === name);

    const currentOuForms = await getFormsListForOu(effectiveOuCode);
    if (!currentOuForms || currentOuForms.some(isSameForm)) return OPEN;

    const otherOus = ouTree.getUnits().filter((ou) => !isSameOuCode(ou.code, effectiveOuCode));
    const otherOusForms = await Promise.all(otherOus.map((ou) => getFormsListForOu(ou.code)));
    const containingOus = otherOus.filter((_, index) => otherOusForms[index]?.some(isSameForm));

    // UO de publication accessibles, ou à défaut leurs descendants accessibles les plus proches : celles dont aucun
    // ancêtre ne propose aussi le formulaire (la liste d'une UO contient les formulaires de ses parentes)
    const candidates = containingOus
      .filter((ou) => !containingOus.some((other) => isOuCodeAncestor(other.code, ou.code)))
      .sort((a, b) => ouCodeDepth(a.code) - ouCodeDepth(b.code));
    if (candidates.length === 0) {
      // Refus seulement si toutes les listes ont été lues : hors ligne ou sur une erreur réseau, on ne peut pas
      // conclure et le chargement du formulaire tranche
      if (!connected || otherOusForms.some((forms) => forms === null)) return OPEN;
      this.logger.info(OuAccessStore.TAG, `Formulaire "${formId ?? name}" absent des UO de l'utilisateur : refus`);
      return UNAUTHORIZED;
    }

    // Plusieurs possibilités : UO de rattachement si elle en fait partie
    return switchTo(candidates.find((ou) => ouTree.isAttachedOu(ou.code)) ?? candidates[0]);
  };

  /**
   * Déclaration (brouillon repris, déclaration dupliquée) : elle se poursuit dans l'UO où elle a été saisie.
   * Sans UO enregistrée, la règle du formulaire s'applique si le formulaire est donné, sinon ouverture directe.
   */
  decisionForDeclaration = async (declarationOuCode: string | undefined, form?: OuFormTarget): Promise<OuDecision> => {
    if (!this.isEnabled()) return OPEN;
    if (!normalizeOuCode(declarationOuCode)) return form ? this.decisionForForm(form) : OPEN;
    return this.decisionForOu(declarationOuCode);
  };

  /** Inbox : sans UO, ouverture directe ; sinon elle s'ouvre dans son UO */
  decisionForInbox = (inboxOuCode: string | undefined): OuDecision => {
    if (!this.isEnabled() || !normalizeOuCode(inboxOuCode)) return OPEN;
    return this.decisionForOu(inboxOuCode);
  };

  /** Ressource rattachée à une UO : ouverture dans l'UO effective, bascule si l'UO est accessible, sinon refus */
  private decisionForOu = (ouCode: string | undefined): OuDecision => {
    const { effectiveOuCode, ouTree } = this.RootStore.LoginStore;
    if (isSameOuCode(ouCode, effectiveOuCode)) return OPEN;

    const ou = ouTree.getNode(ouCode);
    return ou ? switchTo(ou) : UNAUTHORIZED;
  };
}

export default OuAccessStore;
